import { FC, ReactNode, ButtonHTMLAttributes } from 'react'

import styled, { css, RuleSet } from 'styled-components'

import { colors } from 'src/Helpers/constants'

type ButtonVariant = 'filled' | 'outline' | 'subtle' | 'light'
type ButtonColor = 'blue' | 'green' | 'red' | 'gray'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const getButtonStyles = (
  variant: ButtonVariant,
  color: ButtonColor,
  size: ButtonSize,
): RuleSet<object> => {
  const colorMap = {
    blue: { main: colors.fourthBlue, hover: '#1864AB', light: '#E7F5FF' },
    green: { main: '#2F9E44', hover: '#2B8A3E', light: '#D3F9D8' },
    red: { main: '#C92A2A', hover: '#A61E1E', light: '#FFE3E3' },
    gray: { main: '#868E96', hover: '#495057', light: '#F1F3F5' },
  }

  const sizeMap = {
    xs: { padding: '4px 8px', fontSize: '11px', height: '24px' },
    sm: { padding: '6px 12px', fontSize: '12px', height: '32px' },
    md: { padding: '8px 16px', fontSize: '14px', height: '40px' },
    lg: { padding: '12px 20px', fontSize: '16px', height: '48px' },
    xl: { padding: '16px 24px', fontSize: '18px', height: '56px' },
  }

  const col = colorMap[color]
  const sz = sizeMap[size]

  const baseStyles = css`
    padding: ${sz.padding};
    font-size: ${sz.fontSize};
    height: ${sz.height};
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-decoration: none;
    white-space: nowrap;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `

  switch (variant) {
    case 'filled':
      return css`
        ${baseStyles}
        background-color: ${col.main};
        color: white;

        &:hover:not(:disabled) {
          background-color: ${col.hover};
        }
      `
    case 'outline':
      return css`
        ${baseStyles}
        background-color: transparent;
        color: ${col.main};
        border-color: ${col.main};

        &:hover:not(:disabled) {
          background-color: ${col.light};
        }
      `
    case 'subtle':
      return css`
        ${baseStyles}
        background-color: transparent;
        color: ${col.main};

        &:hover:not(:disabled) {
          background-color: ${col.light};
        }
      `
    case 'light':
      return css`
        ${baseStyles}
        background-color: ${col.light};
        color: ${col.main};

        &:hover:not(:disabled) {
          background-color: ${col.main};
          color: white;
        }
      `
    default:
      return baseStyles
  }
}

const StyledButton = styled.button<{
  $variant: ButtonVariant
  $color: ButtonColor
  $size: ButtonSize
}>`
  ${({ $variant, $color, $size }) => getButtonStyles($variant, $color, $size)}
`

const StyledLink = styled.a<{
  $variant: ButtonVariant
  $color: ButtonColor
  $size: ButtonSize
}>`
  ${({ $variant, $color, $size }) => getButtonStyles($variant, $color, $size)}
`

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  color?: ButtonColor
  size?: ButtonSize
  leftSection?: ReactNode
  rightSection?: ReactNode
  component?: 'button' | 'a'
  href?: string
  target?: string
  loading?: boolean
  fullWidth?: boolean
}

export type PrimaryButtonProps = ButtonProps

export const Button: FC<ButtonProps> = ({
  variant = 'filled',
  color = 'blue',
  size = 'md',
  leftSection,
  rightSection,
  children,
  component = 'button',
  href,
  target,
  className,
  loading,
  fullWidth,
  disabled,
  type = 'button',
  ...props
}) => {
  const content = (
    <>
      {leftSection}
      {children}
      {rightSection}
    </>
  )

  if (component === 'a' && href) {
    return (
      <StyledLink
        href={href}
        target={target}
        $variant={variant}
        $color={color}
        $size={size}
        className={className}
        style={{ width: fullWidth ? '100%' : undefined }}
      >
        {content}
      </StyledLink>
    )
  }

  return (
    <StyledButton
      $variant={variant}
      $color={color}
      $size={size}
      className={className}
      disabled={disabled || loading}
      type={type}
      data-loading={loading ? '' : undefined}
      style={{ width: fullWidth ? '100%' : undefined }}
      {...props}
    >
      {content}
    </StyledButton>
  )
}
